import { LoaderCircle } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { Container } from '@skytech/ui/Container'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import CheckIcon from 'mdi-react/CheckIcon'
import CloseIcon from 'mdi-react/CloseIcon'
import { useNavigate, useParams } from 'react-router-dom'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import DataPoints, { DataPoint } from '@/components/DataPoints'
import { LoadingError } from '@/components/LoadingError'
import { LocalDate } from '@/components/LocalDate'
import useUser from '@/hooks/user'
import deviceApplicationResources from '@/modules/applications/device/resources'
import { numberFormat } from '@/util/formatters/numbers'

import { DEVICE_ORDER_STATUS_ID, DEVICE_ORDER_STATUS_NAMES } from './constants'
import DeviceApplicationProduct from './DeviceApplicationProduct'
import queries from './queries'

import './DeviceApplicationPage.styles.scss'

export const DeviceApplicationPage = () => {
  const navigate = useNavigate()
  const translate = useTranslation()
  const queryClient = useQueryClient()
  const params = useParams()

  const deviceApplicationId = Number(params.id)

  if (isNaN(deviceApplicationId)) throw Error(translate('Device application ID is not a number!'))
  const user = useUser()

  const {
    data: deviceOrder,
    isError,
    refetch: updateDeviceOrders,
  } = useQuery(queries.deviceApplication(deviceApplicationId), () =>
    deviceApplicationResources.get(deviceApplicationId)
  )

  const setStatusMutation = useMutation(
    (statusId: number) =>
      deviceApplicationResources.setStatus({ deviceApplicationId: deviceApplicationId, statusId }),
    {
      onSuccess() {
        updateDeviceOrders()
        queryClient.invalidateQueries(queries.deviceApplications(), { refetchType: 'all' })
      },
    }
  )

  if (isError) {
    return (
      <Container onSwipedLeft={() => navigate(-1)}>
        <LoadingError />
      </Container>
    )
  }

  if (!deviceOrder) {
    return (
      <Container onSwipedLeft={() => navigate(-1)}>
        <LoaderCircle />
      </Container>
    )
  }

  const isLeasing = Boolean(deviceOrder.order.leasingDurationMonths)
  const totalAmount = isLeasing
    ? deviceOrder.order.totalLeasingAmount
    : deviceOrder.order.totalAmount
  const deliveryDate = deviceOrder.order.deliveryDate
  const userMayChangeStatus =
    user?.mayApproveOwnApplications || deviceOrder.order.costObjectId !== user?.costObjectId

  return (
    <Container onSwipedLeft={() => navigate(-1)}>
      <div className="device-application">
        <div className="header">
          <Container.Header>
            <Translation id="Device Order" /> {deviceOrder.order.id}
          </Container.Header>

          <h3>
            <Translation
              id={`device_order_status/${DEVICE_ORDER_STATUS_NAMES[deviceOrder.order.status]}`}
            />
          </h3>
        </div>

        {deviceOrder.order.status === DEVICE_ORDER_STATUS_ID.NEW && (
          <div className="device-application__change">
            {userMayChangeStatus && (
              <>
                <Button
                  variant="primary"
                  onClick={() => setStatusMutation.mutate(DEVICE_ORDER_STATUS_ID.PROCESSING)}
                  Icon={<CheckIcon />}
                >
                  <Translation id="Approve" />
                </Button>

                <Button
                  Icon={<CloseIcon />}
                  onClick={() => setStatusMutation.mutate(DEVICE_ORDER_STATUS_ID.DENIED)}
                >
                  <Translation id="Reject" />
                </Button>
              </>
            )}
          </div>
        )}

        <div className="sections">
          <section>
            <DataPoints>
              <h3>
                <Translation id="Details" />
              </h3>

              <DataPoint header={<Translation id="User" />} value={deviceOrder.order.name} />

              <DataPoint
                header={<Translation id="Total cost/ Salary deduction" />}
                value={
                  <>
                    {numberFormat(totalAmount)},- /{' '}
                    {numberFormat(deviceOrder.order.remainderAmount)},-
                  </>
                }
              />
              <DataPoint
                header={<Translation id="Amount for approval" />}
                value={<>{numberFormat(totalAmount - deviceOrder.order.remainderAmount)},-</>}
              />

              <DataPoint
                header={<Translation id="Downpayments" />}
                value={numberFormat(deviceOrder.order.downPayments)}
              />

              <DataPoint
                header={<Translation id="Order date" className="capitalize" />}
                value={<LocalDate date={deviceOrder.order.orderDate} />}
              />

              <DataPoint
                header={<Translation id="Last update" />}
                value={<LocalDate date={deviceOrder.order.orderUpdate} />}
              />

              {!!deliveryDate && (
                <DataPoint
                  header={<Translation id="Delivery date" />}
                  value={<LocalDate date={deliveryDate} />}
                />
              )}

              {deviceOrder.order.status !== 1 && !deliveryDate && (
                <DataPoint
                  header={<Translation id="Estimated delivery" />}
                  value={
                    deviceOrder.order.estimatedDelivery ? (
                      <LocalDate date={deviceOrder.order.estimatedDelivery} />
                    ) : (
                      <Translation id="TBA" />
                    )
                  }
                />
              )}
            </DataPoints>
          </section>

          <section>
            <div className="products-list">
              <h3>
                <Translation id="Products" />
              </h3>

              {deviceOrder.products.map(product => (
                <DeviceApplicationProduct
                  key={product.productId}
                  product={product}
                  isLeasing={isLeasing}
                />
              ))}
            </div>
          </section>
        </div>

        <Button variant="tertiary" onClick={() => navigate(-1)}>
          <Translation id="Back to list" />
        </Button>
      </div>
    </Container>
  )
}
